import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AOS from "aos";
import "aos/dist/aos.css";
import "./blogs.css";
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import LazyImage from '../components/LazyImage';
import blogService from '../services/blogService';
import blogCache from '../services/blogCache';
import buildDetection from '../utils/buildDetection';
import cleanExcerpt from '../utils/cleanExcerpt'; // Clean markdown from excerpts
import { existingBlogData } from '../data/blogData'; // Import shared data

// ULTRA AGGRESSIVE pre-rendering detection - FIRST THING TO RUN
const IMMEDIATE_PRERENDER_CHECK = (() => {
    // Multiple immediate checks
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return true;
    
    const userAgent = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    const isHeadless = userAgent.includes('HeadlessChrome') || 
                      userAgent.includes('puppeteer') || 
                      userAgent.includes('react-snap') ||
                      userAgent.includes('Chrome-Lighthouse');
    
    const hasFlags = typeof window !== 'undefined' && (
        window.__PRERENDER_INJECTED || 
        window.__REACT_SNAP__ || 
        window.__IS_BUILD_TIME__ ||
        window.__HEADLESS_CHROME__
    );
    
    const isHeadlessWindow = typeof window !== 'undefined' && 
                            (window.outerHeight === 0 || window.outerWidth === 0);
    
    return isHeadless || hasFlags || isHeadlessWindow;
})();

// If pre-rendering detected, completely disable all API functionality
if (IMMEDIATE_PRERENDER_CHECK) {
    console.log('🚨 IMMEDIATE PRE-RENDER BLOCK: All API calls disabled');
    
    // Override blogService methods to return empty results
    if (typeof window !== 'undefined') {
        window.__BLOG_API_DISABLED__ = true;
    }
}

// Immediate pre-rendering check - MOST AGGRESSIVE DETECTION
const IS_PRERENDERING = IMMEDIATE_PRERENDER_CHECK || 
                       typeof window === 'undefined' || 
                       typeof navigator === 'undefined' ||
                       (typeof window !== 'undefined' && window.navigator.userAgent.includes('HeadlessChrome')) ||
                       (typeof window !== 'undefined' && window.navigator.userAgent.includes('puppeteer')) ||
                       (typeof window !== 'undefined' && window.__PRERENDER_INJECTED) ||
                       (typeof window !== 'undefined' && window.__REACT_SNAP__) ||
                       (typeof process !== 'undefined' && process.env.NODE_ENV === 'production' && typeof window !== 'undefined' && !window.location.origin.includes('localhost'));

// EMERGENCY OVERRIDE: If we're in a headless environment, completely disable API calls
if (typeof window !== 'undefined' && (
    window.navigator.userAgent.includes('HeadlessChrome') ||
    window.navigator.userAgent.includes('puppeteer') ||
    window.__PRERENDER_INJECTED ||
    window.__REACT_SNAP__ ||
    window.__BLOG_API_DISABLED__
)) {
    console.log('🚨 EMERGENCY OVERRIDE: Headless environment detected - ALL API calls disabled');
    // Override fetch globally to prevent any API calls - SILENT VERSION
    window.fetch = () => Promise.resolve({
        ok: false,
        status: 503,
        statusText: 'Service Unavailable - Pre-rendering',
        json: () => Promise.resolve({}),
        text: () => Promise.resolve('')
    });
}

// Using shared blog data from blogData.js - no need to duplicate here

const Blogs = () => {
    const navigate = useNavigate();
    const [selectedCategory, setSelectedCategory] = useState("All");
    const [searchQuery, setSearchQuery] = useState("");
    // eslint-disable-next-line no-unused-vars
    const [isLoading, setIsLoading] = useState(false);
    
    // ✅ SAFE: Always start with existingBlogData for consistent first render
    const [allBlogs, setAllBlogs] = useState(existingBlogData);
    
    // ✅ SAFE: Filtered blogs also start with existingBlogData
    const [filteredBlogs, setFilteredBlogs] = useState(existingBlogData);
    
    // ✅ SAFE: Load cache AFTER mount in useEffect
    useEffect(() => {
        // Only run in browser
        if (typeof window === 'undefined') return;
        
        // Try to load from cache immediately after mount
        const cachedBlogs = blogCache.getCachedBlogs();
        if (cachedBlogs && cachedBlogs.length > 0) {
            console.log('⚡ INSTANT LOAD: Using cached blogs from localStorage');
            setAllBlogs(cachedBlogs);
        }
    }, []); // Run once on mount

    // 🚀 ZERO FLICKER: Load API data in background while showing cached/existing content
    useEffect(() => {
        // ULTRA AGGRESSIVE PRE-RENDERING CHECK - IMMEDIATE RETURN
        if (IMMEDIATE_PRERENDER_CHECK || 
            IS_PRERENDERING || 
            buildDetection.shouldSkipApiCalls() || 
            (typeof window !== 'undefined' && window.__IS_BUILD_TIME__) ||
            (typeof window !== 'undefined' && window.__BLOG_API_DISABLED__) ||
            (typeof window !== 'undefined' && window.navigator.userAgent.includes('HeadlessChrome')) ||
            (typeof window !== 'undefined' && window.navigator.userAgent.includes('puppeteer'))) {
            console.log('🔄 Pre-rendering detected - COMPLETELY SKIPPING useEffect (no API calls)');
            return;
        }

        // TRIPLE CHECK: If we're in a headless environment, don't proceed
        if (typeof window !== 'undefined' && 
            (window.navigator.userAgent.includes('HeadlessChrome') || 
             window.navigator.userAgent.includes('puppeteer') ||
             window.__PRERENDER_INJECTED ||
             window.__REACT_SNAP__ ||
             window.__HEADLESS_CHROME__)) {
            console.log('🚨 Headless environment detected in useEffect - ABORTING ALL OPERATIONS');
            return;
        }

        const loadBlogsInBackground = async () => {
            try {
                console.log('🔄 Loading fresh blog data in background...');
                
                // Check if we already have fresh cached data
                if (blogCache.isCacheAvailable()) {
                    console.log('✅ Fresh cache available, skipping API call');
                    return;
                }
                
                // Show loader only if we don't have cached data and need to fetch from API
                const cachedBlogs = blogCache.getCachedBlogs();
                if (!cachedBlogs || cachedBlogs.length === 0) {
                    setIsLoading(true);
                }
                
                // Check API health first
                const isHealthy = await blogService.healthCheck();
                
                if (isHealthy) {
                    // Fetch blogs from API
                    const apiBlogs = await blogService.fetchBlogs();
                    
                    if (apiBlogs && apiBlogs.length > 0) {
                        // Transform API blogs to match existing structure
                        const transformedBlogs = apiBlogs.map(blog => blogService.transformApiBlog(blog));
                        
                        // Merge API blogs with existing blogs (API blogs first, then existing)
                        const mergedBlogs = [...transformedBlogs, ...existingBlogData];
                        
                        // Remove duplicates based on title similarity
                        const uniqueBlogs = mergedBlogs.filter((blog, index, self) => 
                            index === self.findIndex(b => 
                                b.title.toLowerCase().trim() === blog.title.toLowerCase().trim()
                            )
                        );
                        
                        // 🔄 SEAMLESS UPDATE: Update content without flicker
                        setAllBlogs(uniqueBlogs);
                        
                        // 🚀 PERFORMANCE BOOST: Initialize cache for instant future loading
                        await blogCache.initialize(uniqueBlogs);
                        await blogCache.preloadCriticalContent();
                        
                        console.log(`✅ Background update complete: ${transformedBlogs.length} new blogs, ${uniqueBlogs.length} total`);
                        console.log('⚡ Blog cache updated - future loads will be instant!');
                    } else {
                        console.log('📝 No new blogs from API, keeping existing content');
                        
                        // Initialize cache with existing blogs
                        await blogCache.initialize(existingBlogData);
                        await blogCache.preloadCriticalContent();
                    }
                } else {
                    console.log('📝 API unavailable, using existing content');
                    
                    // Initialize cache with existing blogs
                    await blogCache.initialize(existingBlogData);
                    await blogCache.preloadCriticalContent();
                }
            } catch (error) {
                console.log('📝 Keeping existing blog content');
                
                // Initialize cache with existing blogs as fallback
                await blogCache.initialize(existingBlogData);
                await blogCache.preloadCriticalContent();
            } finally {
                // Hide loader after API call completes
                setIsLoading(false);
            }
        };

        // Start background loading immediately (no delay for user)
        loadBlogsInBackground();
    }, []);

    useEffect(() => {
        AOS.init({
            duration: 600,
            once: true,
            easing: 'ease-out',
        });

        // Check for search query in URL
        const params = new URLSearchParams(window.location.search);
        const search = params.get('search');
        if (search) {
            setSearchQuery(search);
        }
    }, []);

    useEffect(() => {
        let filtered = allBlogs;

        // Filter by category
        if (selectedCategory !== "All") {
            filtered = filtered.filter(blog => blog.category === selectedCategory);
        }

        // Filter by search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(blog => 
                blog.title.toLowerCase().includes(query) ||
                blog.excerpt.toLowerCase().includes(query) ||
                blog.category.toLowerCase().includes(query) ||
                blog.author.toLowerCase().includes(query)
            );
        }

        setFilteredBlogs(filtered);
    }, [selectedCategory, searchQuery, allBlogs]);

    const categories = ["All", ...new Set(allBlogs.map(blog => blog.category))];

    const handleBlogClick = (slug) => {
        navigate(`/blogs/${slug}`);
    };

    // 🚀 REMOVED LOADING STATE - Content shows instantly!
    // No more loading spinner blocking the user experience

    return (
        <>
            
            <div className="blogs-page">
                <SEOHead 
                    title="Blog | AK Consultants"
                    description="Stay updated with the latest insights from AK Consultants' experts. Read our blog for trends in AI, machine learning, GIS, mobile development, and biomedical engineering."
                />
                <div className="blogs-container">
                    {/* Simple Header */}
                    <div className="blogs-header" data-aos="fade-up">
                        <h1>Knowledge Hub</h1>
                        <p>Insights, innovations, and industry trends from our experts</p>
                    </div>

                    {/* Category Filter */}
                    <div className="category-filter" data-aos="fade-up">
                        {categories.map((category) => (
                            <button
                                key={category}
                                className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                                onClick={() => setSelectedCategory(category)}
                            >
                                {category}
                            </button>
                        ))}
                    </div>

                    {/* Blog Grid */}
                    <div className="blogs-grid">
                        {filteredBlogs.map((blog, index) => (
                            <div 
                                key={`blog-${blog.id}-${blog.slug}`} 
                                className="blog-card" 
                                data-aos="fade-up"
                                onClick={() => handleBlogClick(blog.slug)}
                            >
                                <div className="blog-card-image">
                                    <LazyImage
                                        src={blog.image}
                                        alt={blog.title}
                                        onError={(e) => {
                                            e.target.src = "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800&q=80";
                                        }}
                                    />
                                    <span className="blog-card-category">{blog.category}</span>
                                </div>
                                <div className="blog-card-content">
                                    <h3>{blog.title}</h3>
                                    <p>{cleanExcerpt(blog.shortDescription || blog.excerpt)}</p>
                                    <div className="blog-card-meta">
                                        <div className="meta-left">
                                            <div className="author-info-card">
                                                <div className="author-avatar">
                                                    <i className="fas fa-user"></i>
                                                </div>
                                                <div className="author-details">
                                                    <span className="blog-author">{blog.author}</span>
                                                    <span className="blog-date">{blog.date}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="meta-right">
                                            <span className="blog-read-time">{blog.readTime}</span>
                                            <button className="read-more-link">
                                                Read More →
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <Footer />
            </div>
        </>
    );
};

export default Blogs;